/**
 * What a patient's insurance pays for.
 *
 * The requirement's "dynamic boxes": one per service — emergency, medication,
 * checkups, labs, radiology, surgery, ICU, ward — each with a discount
 * percentage, an optional maximum amount, and a note; 0% means not covered.
 * The hospital adds its own (maternity, optical…). Or, instead of boxes, one
 * flat discount on everything, or a wallet the cover draws down across every
 * service.
 *
 * The numbers are the server's to apply (lib/coverage.js); this only edits
 * them, and shows how much of each capped box is already used so Contracts
 * can see a cap running out before a request hits it.
 */

import { useEffect, useState } from 'react';
import { Badge, Button, ErrorState, Field, Icon, Input, Spinner } from '../../components/common';
import { Tile3D } from '../../components/common/Tile3D';
import { patientsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { formatMoney } from './labels';

const MODES = [
  { id: 'per_service', label: 'تغطية لكل خدمة', hint: 'نسبة وحد أقصى لكل بند', icon: 'layout-grid', tone: 'purple' },
  { id: 'global_discount', label: 'خصم عام ثابت', hint: 'نسبة واحدة على كل الخدمات', icon: 'percent', tone: 'cyan' },
  { id: 'wallet', label: 'رصيد محفظة', hint: 'مبلغ يُخصم من كل الخدمات', icon: 'wallet', tone: 'emerald' },
];

export function CoverageEditor({ patientId, readOnly = false, onSaved }) {
  const { success, error: toastError } = useNotifications();
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customLabel, setCustomLabel] = useState('');

  const load = async () => {
    setError(null);
    try {
      const result = await patientsApi.coverage(patientId);
      setCoverage(result.coverage);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!coverage) return <Spinner label="جارٍ تحميل التغطية" variant="inline" />;

  const setRule = (key, patch) =>
    setCoverage((c) => ({ ...c, rules: c.rules.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    setCoverage((c) => ({
      ...c,
      rules: [...c.rules, { key: `custom_${Date.now()}`, label, custom: true, discountPercent: 0, maxAmount: null, note: null }],
    }));
    setCustomLabel('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await patientsApi.saveCoverage(patientId, {
        mode: coverage.mode,
        rules: coverage.rules,
        globalDiscountPercent: coverage.globalDiscountPercent,
        walletBalance: coverage.walletBalance,
      });
      setCoverage(result.coverage);
      success('تم حفظ التغطية التأمينية');
      onSaved?.(result.coverage);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const used = coverage.usage || {};

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {MODES.map((mode, i) => (
          <Tile3D
            key={mode.id}
            icon={mode.icon}
            label={mode.label}
            hint={mode.hint}
            tone={coverage.mode === mode.id ? mode.tone : 'slate'}
            pulse={coverage.mode === mode.id}
            enter
            delay={i}
            disabled={readOnly}
            onClick={readOnly ? undefined : () => setCoverage((c) => ({ ...c, mode: mode.id }))}
            aria-pressed={coverage.mode === mode.id}
          />
        ))}
      </div>

      {coverage.mode === 'global_discount' && (
        <Field label="نسبة الخصم العام على كل الخدمات (%)">
          <Input
            type="number"
            min="0"
            max="100"
            value={coverage.globalDiscountPercent ?? ''}
            onChange={(e) => setCoverage((c) => ({ ...c, globalDiscountPercent: e.target.value }))}
            disabled={readOnly}
          />
        </Field>
      )}

      {coverage.mode === 'wallet' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="رصيد المحفظة الكلي (ج.م)">
            <Input
              type="number"
              min="0"
              value={coverage.walletBalance ?? ''}
              onChange={(e) => setCoverage((c) => ({ ...c, walletBalance: e.target.value }))}
              disabled={readOnly}
            />
          </Field>
          <div className="flex items-end">
            <Badge className="border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">
              <Icon name="wallet" size={13} /> المتبقي: {formatMoney(coverage.walletRemaining ?? coverage.walletBalance ?? 0)}
            </Badge>
          </div>
        </div>
      )}

      {coverage.mode === 'per_service' && (
        <div className="space-y-2">
          <div className="hidden grid-cols-[1.3fr_0.7fr_0.9fr_1.6fr] gap-2 px-2 text-[11px] font-bold text-slate-500 sm:grid">
            <span>الخدمة</span>
            <span>نسبة التغطية %</span>
            <span>الحد الأقصى (ج.م)</span>
            <span>ملاحظات</span>
          </div>
          {coverage.rules.map((rule) => {
            const pct = Number(rule.discountPercent) || 0;
            const consumed = Number(used[rule.key] || 0);
            return (
              <div
                key={rule.key}
                className={`grid grid-cols-2 items-center gap-2 rounded-xl border p-2 sm:grid-cols-[1.3fr_0.7fr_0.9fr_1.6fr] ${
                  pct > 0 ? 'border-purple-500/25 bg-purple-500/[0.06]' : 'border-white/5 bg-white/[0.02]'
                }`}
              >
                <span className="col-span-2 flex items-center gap-2 text-sm font-bold text-white sm:col-span-1">
                  {rule.label}
                  {rule.custom && <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200">إضافي</Badge>}
                  {pct === 0 && <Badge className="border-slate-500/30 bg-slate-500/10 text-slate-400">غير مشمول</Badge>}
                </span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  aria-label={`نسبة تغطية ${rule.label}`}
                  value={rule.discountPercent ?? 0}
                  onChange={(e) => setRule(rule.key, { discountPercent: e.target.value })}
                  disabled={readOnly}
                />
                <div>
                  <Input
                    type="number"
                    min="0"
                    placeholder="بلا حد"
                    aria-label={`الحد الأقصى ل${rule.label}`}
                    value={rule.maxAmount ?? ''}
                    onChange={(e) => setRule(rule.key, { maxAmount: e.target.value === '' ? null : e.target.value })}
                    disabled={readOnly}
                  />
                  {rule.maxAmount !== null && rule.maxAmount !== undefined && rule.maxAmount !== '' && (
                    <p className="mt-0.5 text-[10px] text-slate-400">
                      مستخدم {formatMoney(consumed)} من {formatMoney(rule.maxAmount)}
                    </p>
                  )}
                </div>
                <Input
                  className="col-span-2 sm:col-span-1"
                  placeholder="ملاحظة (مثال: يشمل الأدوية الأساسية فقط)"
                  value={rule.note || ''}
                  onChange={(e) => setRule(rule.key, { note: e.target.value })}
                  disabled={readOnly}
                />
              </div>
            );
          })}

          {!readOnly && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
                placeholder="خدمة إضافية (مثال: ولادة، بصريات)"
                className="min-w-0 flex-1"
              />
              <Button variant="subtle" icon="plus" onClick={addCustom}>
                إضافة خدمة
              </Button>
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-end border-t border-white/5 pt-4">
          <Button icon="save" loading={saving} onClick={save}>
            حفظ التغطية
          </Button>
        </div>
      )}
    </div>
  );
}

export default CoverageEditor;
